// ============================================================================
// SPAIN TAX CALCULATION - IRPF 2025
// Fuente: Agencia Tributaria - Ley 35/2006 del IRPF
// Seguridad Social: Orden PJC/178/2025 (BOE 26/02/2025)
// https://sede.agenciatributaria.gob.es
// ============================================================================

import { TaxCalculationResult, TaxBracket, DeductionBreakdown } from "./types";
import { calculateTaxByBrackets } from "./utils";

// ============================================================================
// CONFIGURATION
// ============================================================================

interface SpainTaxConfig {
  taxBrackets: TaxBracket[];
  socialSecurity: {
    contingenciasComunes: number;
    desempleoIndefinido: number;
    desempleoTemporal: number;
    formacionProfesional: number;
    mei: number;
    baseMaximaMensual: number;
    baseMinimaMensual: number;
  };
  minimosPersonales: {
    general: number;
    mayor65: number;
    mayor75: number;
  };
  minimosPorDescendientes: number[];
  minimosPorAscendientes: {
    mayor65: number;
    mayor75: number;
  };
  minimosDiscapacidad: {
    grado33a65: number;
    grado65omas: number;
    gastoAsistencia: number;
  };
  reduccionRendimientosTrabajo: {
    limite1: number;
    limite2: number;
    reduccionMaxima: number;
    reduccionMinima: number;
  };
  gastosDeducibles: number;
}

export const SPAIN_TAX_CONFIG_2025: SpainTaxConfig = {
  // Tramos IRPF 2025 - Tipo TOTAL (Estatal + Autonómico estándar)
  taxBrackets: [
    { min: 0, max: 12450, rate: 19 },
    { min: 12450, max: 20200, rate: 24 },
    { min: 20200, max: 35200, rate: 30 },
    { min: 35200, max: 60000, rate: 37 },
    { min: 60000, max: 300000, rate: 45 },
    { min: 300000, max: Infinity, rate: 47 },
  ],

  socialSecurity: {
    // Tipos de cotización del trabajador 2025
    // Fuente: Orden PJC/178/2025 (BOE 26/02/2025)
    contingenciasComunes: 4.70,
    desempleoIndefinido: 1.55,
    desempleoTemporal: 1.60,
    formacionProfesional: 0.10,
    mei: 0.13, // MEI 2025: 0,80% total (0,67% empresa + 0,13% trabajador)
    // Bases de cotización 2025
    baseMaximaMensual: 4909.50,
    baseMinimaMensual: 1381.20,
  },

  minimosPersonales: {
    general: 5550,
    mayor65: 6700, // 5550 + 1150
    mayor75: 8100, // 5550 + 1150 + 1400
  },

  // Por descendiente (menores de 25 años o discapacitados)
  minimosPorDescendientes: [
    2400, // Primero
    2700, // Segundo
    4000, // Tercero
    4500, // Cuarto y siguientes
  ],

  minimosPorAscendientes: {
    mayor65: 1150,
    mayor75: 2550, // 1150 + 1400
  },

  minimosDiscapacidad: {
    grado33a65: 3000,
    grado65omas: 9000,
    gastoAsistencia: 3000,
  },

  // Reducción por obtención de rendimientos del trabajo (Art. 20 LIRPF)
  reduccionRendimientosTrabajo: {
    limite1: 14047.5,
    limite2: 19747.5,
    reduccionMaxima: 6498,
    reduccionMinima: 0,
  },

  gastosDeducibles: 2000, // Art. 19.2.f LIRPF
};

// ============================================================================
// SOCIAL SECURITY CALCULATION
// ============================================================================

export interface SpainSocialSecurityParams {
  grossIncome: number;
  contractType: string;
  numberOfPayments?: number;
}

/**
 * Calcula la Seguridad Social para España
 * 
 * Datos 2025 según Orden PJC/178/2025 (BOE 26/02/2025):
 * - Contingencias comunes: 4,70% (trabajador)
 * - Desempleo indefinido: 1,55% (trabajador)
 * - Formación profesional: 0,10% (trabajador)
 * - MEI: 0,13% (trabajador)
 * - Base máxima: 4.909,50€/mes (58.914€/año)
 * - Base mínima: 1.381,20€/mes
 * 
 * Total trabajador: 6,48%
 */
export function calculateSpainSocialSecurity({
  grossIncome,
  contractType,
}: SpainSocialSecurityParams): number {
  const config = SPAIN_TAX_CONFIG_2025.socialSecurity;

  // Salario mensual
  const salarioMensual = grossIncome / 12;

  // Base de cotización mensual (con topes)
  const baseCotizacionMensual = Math.min(
    Math.max(salarioMensual, config.baseMinimaMensual),
    config.baseMaximaMensual
  );

  // Base de cotización anual efectiva
  const baseCotizacionAnual = baseCotizacionMensual * 12;

  // Porcentaje de desempleo según tipo de contrato
  const desempleo = contractType === "temporary" 
    ? config.desempleoTemporal 
    : config.desempleoIndefinido;

  // Porcentaje total trabajador 2025: 6,48%
  const porcentajeTotal = 
    config.contingenciasComunes +
    desempleo +
    config.formacionProfesional +
    config.mei;

  return (baseCotizacionAnual * porcentajeTotal) / 100;
}

// ============================================================================
// IRPF CALCULATION
// ============================================================================

export interface SpainIRPFParams {
  grossIncome: number;
  socialSecurityDeduction: number;
  age: number;
  familyStatus: string;
  dependents: number;
  hasDisability: boolean;
  disabilityDegree: number;
  geographicMobility: boolean;
  movedResidence: boolean;
}

export interface SpainIRPFResult {
  irpf: number;
  taxRate: number;
  minimoPersonalFamiliar: number;
}

/**
 * Calcula el IRPF para España según la normativa vigente
 * 
 * Este cálculo simula las retenciones de IRPF en nómina
 * Fuente: Reglamento del IRPF (RD 439/2007) y Orden HAC 2024/2025
 */
export function calculateSpainIRPF({
  grossIncome,
  socialSecurityDeduction,
  age,
  familyStatus,
  dependents,
  hasDisability,
  disabilityDegree,
  geographicMobility,
  movedResidence,
}: SpainIRPFParams): SpainIRPFResult {
  const config = SPAIN_TAX_CONFIG_2025;

  // 1. RENDIMIENTO NETO DEL TRABAJO
  const rendimientoNeto = grossIncome - socialSecurityDeduction - config.gastosDeducibles;

  // 2. REDUCCIÓN POR RENDIMIENTOS DEL TRABAJO (Art. 20 LIRPF)
  let reduccionRendimientos = 0;
  if (rendimientoNeto <= config.reduccionRendimientosTrabajo.limite1) {
    reduccionRendimientos = config.reduccionRendimientosTrabajo.reduccionMaxima;
  } else if (rendimientoNeto < config.reduccionRendimientosTrabajo.limite2) {
    // Reducción decreciente: 6.498 - [1,14 × (Rendimiento neto - 14.047,5)]
    const exceso = rendimientoNeto - config.reduccionRendimientosTrabajo.limite1;
    reduccionRendimientos = Math.max(0, config.reduccionRendimientosTrabajo.reduccionMaxima - (1.14 * exceso));
  }

  // Reducción adicional por movilidad geográfica (2.000€ adicionales primer año)
  if (geographicMobility && movedResidence) {
    reduccionRendimientos += 2000;
  }

  // 3. BASE LIQUIDABLE GENERAL
  const baseLiquidableGeneral = Math.max(0, rendimientoNeto - reduccionRendimientos);

  // 4. MÍNIMO PERSONAL Y FAMILIAR
  // 4.1 Mínimo del contribuyente
  let minimoPersonal = config.minimosPersonales.general;
  if (age >= 75) {
    minimoPersonal = config.minimosPersonales.mayor75;
  } else if (age >= 65) {
    minimoPersonal = config.minimosPersonales.mayor65;
  }

  // 4.2 Mínimo por descendientes
  let minimoDescendientes = 0;
  for (let i = 0; i < Math.min(dependents, 10); i++) {
    const index = Math.min(i, config.minimosPorDescendientes.length - 1);
    minimoDescendientes += config.minimosPorDescendientes[index];
  }

  // 4.3 Mínimo por discapacidad del contribuyente
  let minimoDiscapacidad = 0;
  if (hasDisability) {
    if (disabilityDegree >= 65) {
      minimoDiscapacidad = config.minimosDiscapacidad.grado65omas;
    } else if (disabilityDegree >= 33) {
      minimoDiscapacidad = config.minimosDiscapacidad.grado33a65;
    }
  }

  const minimoPersonalFamiliar = minimoPersonal + minimoDescendientes + minimoDiscapacidad;

  // 5. CÁLCULO DE LA CUOTA ÍNTEGRA
  // En España, el mínimo personal y familiar NO se resta de la base,
  // sino que se calcula el impuesto sobre ambos y se resta
  const cuotaBaseLiquidable = calculateTaxByBrackets(baseLiquidableGeneral, config.taxBrackets);
  const cuotaMinimoPersonalFamiliar = calculateTaxByBrackets(minimoPersonalFamiliar, config.taxBrackets);

  // Cuota íntegra = Cuota sobre base - Cuota sobre mínimo (nunca negativa)
  const cuotaIntegra = Math.max(0, cuotaBaseLiquidable - cuotaMinimoPersonalFamiliar);

  // Tipo efectivo de retención
  const taxRate = grossIncome > 0 ? (cuotaIntegra / grossIncome) * 100 : 0;

  return {
    irpf: cuotaIntegra,
    taxRate,
    minimoPersonalFamiliar,
  };
}

// ============================================================================
// COMBINED CALCULATION
// ============================================================================

export interface SpainTaxParams {
  grossIncome: number;
  contractType: string;
  age: number;
  familyStatus: string;
  dependents: number;
  hasDisability: boolean;
  disabilityDegree: number;
  geographicMobility: boolean;
  movedResidence: boolean;
}

/**
 * Calcula todos los impuestos y cotizaciones para España
 * Devuelve breakdown con nombres en español
 */
export function calculateSpainTax(params: SpainTaxParams): TaxCalculationResult {
  const socialSecurity = calculateSpainSocialSecurity({
    grossIncome: params.grossIncome,
    contractType: params.contractType,
  });

  const irpfResult = calculateSpainIRPF({
    ...params,
    socialSecurityDeduction: socialSecurity,
  });

  // Build breakdown with Spanish labels
  const breakdown: DeductionBreakdown[] = [];

  // IRPF
  if (irpfResult.irpf > 0) {
    breakdown.push({
      id: "irpf",
      amount: irpfResult.irpf,
      rate: irpfResult.taxRate,
      type: "tax",
    });
  }

  // Seguridad Social
  if (socialSecurity > 0) {
    breakdown.push({
      id: "seguridad_social",
      amount: socialSecurity,
      rate: params.grossIncome > 0 ? (socialSecurity / params.grossIncome) * 100 : 0,
      type: "social",
    });
  }

  return {
    tax: irpfResult.irpf,
    taxRate: irpfResult.taxRate,
    socialSecurity,
    allowances: irpfResult.minimoPersonalFamiliar,
    breakdown,
  };
}
